import React, { useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import ResetPassword from '../ResetPassword/ResetPassword'
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client'
import {
  CircularProgress,
  Menu,
  MenuItem,
  Modal,
  Typography,
  AppBar,
  Box,
  Toolbar,
  Divider,
  FormControl,
  Select
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import NotificationBell from '../Notifications/NotificationBell'
import TranslateRounded from '@mui/icons-material/TranslateRounded'
import LockResetRounded from '@mui/icons-material/LockResetRounded'
import LogoutRounded from '@mui/icons-material/LogoutRounded'
import PersonRounded from '@mui/icons-material/PersonRounded'
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded'
import depth, { cardSx } from '../../utils/depth'
import ConfigurableValues from '../../config/constants'
import { updateProfileImage } from '../../apollo/mutations'

const UPDATE_PROFILE_IMAGE = gql`
  ${updateProfileImage}
`
// Only what the avatar needs; normalised by _id, so a logo changed on the
// store profile page shows up here without a refetch.
const STORE_AVATAR = gql`
  query StoreAvatar($id: String) {
    restaurant(id: $id) {
      _id
      name
      image
    }
  }
`

// Photo when there is one, otherwise a neutral person placeholder.
function ProfileAvatar({ src, size, radius, loading }) {
  return (
    <Box
      sx={{
        position: 'relative',
        height: size,
        width: size,
        flexShrink: 0,
        borderRadius: radius,
        overflow: 'hidden',
        bgcolor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      {src ? (
        <img
          alt=""
          src={src}
          style={{ height: '100%', width: '100%', objectFit: 'cover' }}
        />
      ) : (
        <PersonRounded sx={{ fontSize: size * 0.6, color: 'grey.500' }} />
      )}
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <CircularProgress size={size * 0.45} />
        </Box>
      ) : null}
    </Box>
  )
}

function AdminNavbar(props) {
  const client = useApolloClient()
  const [modal, setModal] = useState(false)
  const [language, setLanguage] = useState(
    localStorage.getItem('borku-language') || 'en'
  )
  const [anchorEl, setAnchorEl] = useState(null) // Define anchorEl state
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const [saveProfileImage] = useMutation(UPDATE_PROFILE_IMAGE)

  const { t, i18n } = props

  const toggleModal = () => {
    setModal(prev => !prev)
  }

  const handleMenu = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChangeLanguage = event => {
    const newLanguage = event.target.value
    setLanguage(newLanguage)
    localStorage.setItem('borku-language', newLanguage)
    i18n.changeLanguage(newLanguage)
    handleClose()
  }

  const user = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku'))
    : null
  const vendor = user ? user.userType === 'VENDOR' : false
  const userType = user?.userType || ''
  const displayName = user?.name || user?.email || 'Borku Admin'
  const roleLabel = userType
    ? userType.charAt(0) + userType.slice(1).toLowerCase()
    : ''

  // A store account is represented by its store's image; everyone else
  // (admin, influencer, staff) has a personal photo they can change here.
  const storeId =
    localStorage.getItem('restaurantId') || user?.restaurants?.[0]?._id
  const { data: storeData } = useQuery(STORE_AVATAR, {
    variables: { id: storeId },
    skip: !vendor || !storeId
  })
  const storeImage =
    storeData?.restaurant?.image ||
    user?.restaurants?.find(r => r._id === storeId)?.image ||
    ''
  const [profileImage, setProfileImage] = useState(user?.image || '')
  const avatarSrc = vendor ? storeImage : profileImage

  const pickPhoto = () => {
    setPhotoError('')
    fileInputRef.current && fileInputRef.current.click()
  }

  const handlePhotoSelected = event => {
    const file = event.target.files && event.target.files[0]
    event.target.value = '' // let the same file be picked again after an error
    if (!file) return
    if (!/\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
      setPhotoError(t('PhotoUploadFailed'))
      return
    }
    const reader = new FileReader()
    reader.onloadend = async () => {
      setUploading(true)
      try {
        const result = await fetch(`${CLOUDINARY_UPLOAD_URL}/image/upload`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            file: reader.result,
            upload_preset: CLOUDINARY_FOOD
          })
        })
        const { secure_url: url } = await result.json()
        if (!url) throw new Error('Upload failed')
        await saveProfileImage({ variables: { image: url } })
        setProfileImage(url)
        // Keep the stored session in step so the photo survives a reload.
        const stored = JSON.parse(localStorage.getItem('user-borku') || '{}')
        localStorage.setItem(
          'user-borku',
          JSON.stringify({ ...stored, image: url })
        )
      } catch (e) {
        console.log(e)
        setPhotoError(t('PhotoUploadFailed'))
      } finally {
        setUploading(false)
      }
    }
    reader.readAsDataURL(file)
  }

  // First breadcrumb crumb: which area of the panel the current route lives in.
  const areaLabel =
    props.match.path === '/restaurant'
      ? t('RESTAURANT')
      : props.match.path === '/admin'
      ? t('RESTAURANT')
      : t('WORKSPACE')
  const pageLabel = props.match.path === '/restaurant' ? '' : t(props.brandText)

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        flexGrow: 1,
        boxShadow: 0
      }}>
      <AppBar
        position="static"
        sx={{ bgcolor: 'transparent', boxShadow: 0, color: 'text.primary' }}>
        <Toolbar sx={{ px: { sm: 3, md: 4 }, minHeight: { sm: 72 } }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.disabled',
                textTransform: 'capitalize',
                fontWeight: 500
              }}>
              {areaLabel.toLowerCase()}
            </Typography>
            {pageLabel ? (
              <>
                <Typography variant="body2" sx={{ color: 'grey.400', mx: 1 }}>
                  /
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {pageLabel}
                </Typography>
              </>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationBell />

            <Box
              onClick={handleMenu}
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.25,
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                borderRadius: '999px',
                padding: '4px 12px 4px 4px',
                height: 44,
                '&:hover': { borderColor: 'grey.400' }
              }}>
              <ProfileAvatar
                src={avatarSrc}
                size={34}
                radius="50%"
                loading={uploading}
              />
              <Box sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.25,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {displayName}
                </Typography>
                {roleLabel ? (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'text.disabled',
                      lineHeight: 1.3
                    }}>
                    {roleLabel}
                  </Typography>
                ) : null}
              </Box>
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: 'text.disabled' }}
              />
            </Box>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  ...cardSx,
                  mt: 1,
                  width: 290,
                  p: 1,
                  '& .MuiMenuItem-root': {
                    borderRadius: '10px',
                    fontSize: 14,
                    fontWeight: 600,
                    gap: 1.25,
                    minHeight: 42
                  }
                }
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  mb: 1,
                  borderRadius: '12px',
                  background: depth.headerBackground
                }}>
                <Box
                  onClick={vendor ? undefined : pickPhoto}
                  title={vendor ? undefined : t('ChangePhoto')}
                  sx={{
                    position: 'relative',
                    cursor: vendor ? 'default' : 'pointer',
                    borderRadius: '14px',
                    boxShadow: '0 6px 14px -6px rgba(16,24,40,0.45)'
                  }}>
                  <ProfileAvatar
                    src={avatarSrc}
                    size={44}
                    radius="14px"
                    loading={uploading}
                  />
                  {vendor ? null : (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -4,
                        bottom: -4,
                        height: 20,
                        width: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        border: '2px solid #fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                      <PhotoCameraRounded
                        sx={{ fontSize: 11, color: 'common.white' }}
                      />
                    </Box>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'text.primary'
                    }}>
                    {displayName}
                  </Typography>
                  {roleLabel ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        mt: 0.5,
                        px: 1,
                        py: 0.1,
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: 'primary.light',
                        color: 'primary.main'
                      }}>
                      {roleLabel}
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 0.75
                }}>
                <TranslateRounded
                  sx={{ fontSize: 20, color: 'text.secondary' }}
                />
                <FormControl fullWidth size="small">
                  <Select
                    value={language}
                    onChange={handleChangeLanguage}
                    sx={{
                      color: 'text.primary',
                      fontSize: 14,
                      borderRadius: '10px',
                      bgcolor: '#FAFAFB'
                    }}>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="ar">Arabic</MenuItem>
                    <MenuItem value="de">Deutsche</MenuItem>
                    <MenuItem value="zh">中文</MenuItem>
                    <MenuItem value="km">ភាសាខ្មែរ</MenuItem>
                    <MenuItem value="fr">français</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Divider sx={{ my: 1 }} />
              {vendor ? null : (
                <MenuItem
                  disabled={uploading}
                  onClick={e => {
                    e.preventDefault()
                    pickPhoto()
                  }}>
                  <PhotoCameraRounded
                    sx={{ fontSize: 20, color: 'text.secondary' }}
                  />
                  {profileImage ? t('ChangePhoto') : t('UploadPhoto')}
                </MenuItem>
              )}
              {photoError ? (
                <Typography
                  sx={{ px: 1.5, pb: 0.5, fontSize: 12, color: 'error.main' }}>
                  {photoError}
                </Typography>
              ) : null}
              {vendor ? (
                <MenuItem
                  onClick={e => {
                    e.preventDefault()
                    toggleModal()
                  }}>
                  <LockResetRounded
                    sx={{ fontSize: 20, color: 'text.secondary' }}
                  />
                  {t('ResetPassword')}
                </MenuItem>
              ) : null}
              <MenuItem
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: 'rgba(220,38,38,0.06)' }
                }}
                onClick={e => {
                  e.preventDefault()
                  localStorage.removeItem('user-borku')
                  localStorage.removeItem('restaurant_id')
                  localStorage.removeItem('restaurantId')
                  // The notification feed is per account/store, so it must not
                  // survive into the next session on a shared machine.
                  Object.keys(localStorage)
                    .filter(key => key.startsWith('borku-notifications'))
                    .forEach(key => localStorage.removeItem(key))
                  client.clearStore()
                  props.history.push('/auth/login')
                }}>
                <LogoutRounded sx={{ fontSize: 20 }} />
                {t('Logout')}
              </MenuItem>
            </Menu>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              onChange={handlePhotoSelected}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        open={modal}
        onClose={() => {
          toggleModal()
        }}>
        <ResetPassword />
      </Modal>
    </Box>
  )
}

export default withTranslation()(AdminNavbar)
